import type { GameStateView, Intent, RuleSet, Seat, TileId, PlayerCount, Stamp } from '@mj/protocol';
import type { ServerMsg, GameEventWire, RoomState, SeatList } from '@mj/protocol';
import type { GameEvent } from '@mj/engine';
import { EventJournal, type HandResult, type WaitKind } from './tableGame';
import type { PlayTable } from './playTable';
import { WsClient, type WsStatus } from '../net/wsClient';
import * as vh from './viewHelpers';

/** Mints a FRESH seatTicket for the SAME seat via the matchmaker's idempotent /api/rooms/join. Supplied by
 *  the lobby (which owns the server URL + auth token). Returns the new ticket string. */
export type RoomJoiner = (roomCode: string) => Promise<string>;

export interface RemoteTableOpts {
  url: string;
  seatTicket: string;
  ruleSet: RuleSet;
  players: PlayerCount;
  /** the seat this client was assigned (from RoomTicketResp.seat). */
  mySeat: Seat;
  /** initial room state (from RoomTicketResp.room) so the waiting room shows the code/seats immediately,
   *  before the first server-pushed `roomState` arrives over the WS. */
  room?: RoomState;
  WebSocketCtor?: typeof WebSocket;
  /** Reconnect support: re-mint a ticket on socket drop. Omitted → no rejoin (e.g. unit tests / solo). */
  rejoin?: RoomJoiner;
}

/** Lobby vs live board: a remote table starts in the WAITING phase (the host hasn't started the match yet)
 *  and flips to PLAYING on the first snapshot — the play surface renders the WaitingRoom vs the board on it. */
export type RemotePhase = 'waiting' | 'playing';

const EMPTY_VIEW = Object.freeze({
  wallCount: 0,
  deadWallCount: 0,
  doraIndicators: [] as TileId[],
  uraDoraIndicators: null,
  roundWind: 'z1',
  dealer: 0 as Seat,
  turn: 0 as Seat,
  phase: 'deal',
  honba: 0,
  riichiPot: 0,
  players: [] as GameStateView['players'],
}) as GameStateView;

export class RemoteTable implements PlayTable {
  readonly mode = 'online' as const;
  readonly humanSeat: Seat;
  readonly journal = new EventJournal();
  lastError: string | null = null;
  /** terminal error (welcome ok:false / error fatal:true) — reconnect is stopped and the UI shows it. */
  fatal: string | null = null;

  private readonly initialPlayers: PlayerCount;
  private readonly fallbackRuleSet: RuleSet;
  private readonly listeners = new Set<() => void>();
  private readonly ws: WsClient;
  // waiting-lobby state: phase + the live seat list / room code (pushed via `roomState`). phase flips to
  // 'playing' on the first snapshot (the host started the match).
  private phaseN: RemotePhase = 'waiting';
  private roomState: RoomState | null = null;
  private snap: GameStateView | null = null;
  private drawPeekTile: TileId | null = null;
  private waitKind: WaitKind = 'handOver';
  private legal: Intent[] = [];
  private res: HandResult | null = null;
  private kyokuN = 0;
  private resultDetail: GameEventWire | null = null;
  // final standings from the last gameOver frame; retained while the room is reset to the rematch lobby
  // (cleared when a fresh match snapshot arrives). Drives the gameOver standings table.
  private finalScoresN: number[] | null = null;
  private connStatusN: WsStatus = 'connecting';
  // HOLE #12: recently-received stamps (volatile, display-only — never persisted/replayed). Keep a
  // short capped list; SoloMahjong renders the latest per seat as a transient bubble.
  private recentStamps: { seat: Seat; stamp: Stamp; at: number }[] = [];

  constructor(opts: RemoteTableOpts) {
    this.initialPlayers = opts.players;
    this.humanSeat = opts.mySeat;
    this.fallbackRuleSet = opts.ruleSet;
    this.roomState = opts.room ?? null;
    this.ws = new WsClient({
      url: opts.url,
      seatTicket: opts.seatTicket,
      onMessage: (msg) => this.onMessage(msg),
      onStatus: (s) => {
        this.connStatusN = s;
        this.notify();
      },
      ...(opts.WebSocketCtor ? { WebSocketCtor: opts.WebSocketCtor } : {}),
      ...(opts.rejoin
        ? {
            rejoin: async () => {
              const code = this.roomState?.roomCode ?? opts.room?.roomCode;
              if (!code) throw new Error('no room code for rejoin');
              return opts.rejoin!(code);
            },
          }
        : {}),
    });
  }

  get kyoku(): number {
    return this.kyokuN;
  }

  /** the seat count: server-authoritative while waiting (host may change it via updateConfig), then fixed. */
  get players(): PlayerCount {
    if (this.phaseN === 'waiting' && this.roomState) return this.roomState.players as PlayerCount;
    return this.initialPlayers;
  }

  /** the active ruleset: roomState-authoritative once known (host-editable while waiting), else the seed. */
  get ruleSet(): RuleSet {
    return this.roomState?.ruleSet ?? this.fallbackRuleSet;
  }

  /** 'waiting' until the host starts the match (first snapshot) — drives the WaitingRoom vs board. */
  get phase(): RemotePhase {
    return this.phaseN;
  }

  /** the controlling seat (from roomState); only the host may updateConfig/start. null before any roomState. */
  get host(): Seat | null {
    return this.roomState?.host ?? null;
  }

  /** true when this client controls the room. Derived from roomState.host; seat-0 default before it arrives. */
  get isHost(): boolean {
    return this.roomState != null ? this.roomState.host === this.humanSeat : this.humanSeat === 0;
  }

  /** live socket connection status, surfaced for the connection badge / "reconnecting" UI. */
  get connStatus(): WsStatus {
    return this.connStatusN;
  }

  /** final standings from the last gameOver, retained through the rematch-lobby reset (else null). */
  get finalScores(): number[] | null {
    return this.finalScoresN;
  }

  /** the shareable room code (waiting lobby), or '' before any roomState is known. */
  get roomCode(): string {
    return this.roomState?.roomCode ?? '';
  }

  /** live seat occupancy (human/bot/empty) for the waiting-room seat list. */
  get seats(): SeatList {
    return this.roomState?.seats ?? [];
  }

  /** whether empty seats will be filled with CPUs at start (host's create-time default). */
  get botFill(): boolean {
    return this.roomState?.botFill ?? false;
  }

  /** true while seat `s` is inside the reconnect grace (socket dropped, not yet a bot). From roomState. */
  offlineSeat(s: Seat): boolean {
    return this.roomState?.seats.find((e) => e.seat === s)?.offline === true;
  }

  /** Host-only: leave the waiting lobby and start the match (`botFill` fills empty seats with CPUs). */
  start(botFill: boolean): void {
    if (this.fatal) return;
    this.ws.sendStart(botFill);
  }

  /** Host-only, WAITING only: change the player count and/or ruleset. The UI gates this to the host; the
   *  send is optimistic (the server replies NOT_HOST/ROOM_STARTED → surfaced via lastError if rejected). */
  updateConfig(players?: PlayerCount, ruleSet?: RuleSet): void {
    if (this.fatal) return;
    this.ws.sendUpdateConfig(players, ruleSet);
  }

  /** Graceful per-seat leave: tell the server (`{t:'leave'}`) then close the socket. The single UI teardown
   *  (exitOnline) also calls leaveOnline() to clear the session holder — leave() only does the wire+dispose. */
  leave(): void {
    if (!this.fatal) this.ws.sendLeave();
    this.dispose();
  }

  get wait(): WaitKind {
    return this.waitKind;
  }

  get result(): HandResult | null {
    return this.res;
  }

  view(): GameStateView {
    return this.snap ?? EMPTY_VIEW;
  }

  humanMoves(): Intent[] {
    return this.waitKind === 'turn' || this.waitKind === 'call' ? this.legal : [];
  }

  humanDrawn(): TileId | null {
    if (this.waitKind !== 'turn' || !this.snap) return null;
    if (this.snap.turn !== this.humanSeat) return null;
    if (this.snap.phase === 'draw' && this.snap.turn === this.humanSeat) return this.drawPeekTile;
    const hand = this.myHand();
    const entries = this.journal.sliceFrom(0);
    for (let i = entries.length - 1; i >= 0; i--) {
      const entry = entries[i]!;
      if (entry.ev.kind !== 'engine') continue;
      const e = entry.ev.e;
      if (e.event === 'draw' && e.seat === this.humanSeat && e.tile !== undefined && hand.includes(e.tile)) return e.tile;
    }
    return null;
  }

  humanCanTsumo(): boolean {
    if (!this.canUseDecisionHelpers()) return false;
    const drawn = this.snap!.phase === 'draw' && this.snap!.turn === this.humanSeat ? this.drawPeekTile : this.humanDrawn();
    return vh.canTsumo(this.myHand(), this.myMelds(), this.snap!.phase, drawn);
  }

  humanAnkanTiles(): TileId[] {
    if (!this.canUseDecisionHelpers()) return [];
    return vh.ankanTiles(this.decisionHand());
  }

  humanShouminkanTiles(): TileId[] {
    if (!this.canUseDecisionHelpers()) return [];
    return vh.shouminkanTiles(this.decisionHand(), this.myMelds());
  }

  humanNukiTiles(): TileId[] {
    if (!this.canUseDecisionHelpers()) return [];
    return vh.nukiTiles(this.decisionHand(), this.ruleSet);
  }

  humanFlowerTiles(): TileId[] {
    if (!this.canUseDecisionHelpers()) return [];
    return vh.flowerTiles(this.decisionHand(), this.ruleSet);
  }

  humanCanWindKan(): boolean {
    if (!this.canUseDecisionHelpers()) return false;
    return vh.canWindKan(this.decisionHand(), this.ruleSet);
  }

  cpuTargetName(_seat: Seat): string | null {
    return null;
  }

  act(intent: Intent): { ok: boolean; error?: string } {
    if (this.fatal) return { ok: false, error: this.fatal };
    this.ws.sendIntent(intent);
    return { ok: true };
  }

  pass(): void {
    if (this.fatal) return;
    this.ws.sendIntent({ kind: 'pass' });
  }

  nextHand(): void {
    if (this.fatal) return;
    this.ws.ready();
  }

  /** Send a preset stamp (online emote). No-op while fatal. */
  sendStamp(stamp: Stamp): void {
    if (this.fatal) return;
    this.ws.sendStamp(stamp);
  }

  /** The most recent stamp per seat (display-only), newest last. Volatile. */
  get stamps(): readonly { seat: Seat; stamp: Stamp; at: number }[] {
    return this.recentStamps;
  }

  setSkipCalls(_b: boolean): void {}
  setAutoWin(_b: boolean): void {}
  setAutoNuki(_b: boolean): void {}

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  dispose(): void {
    this.ws.close();
    this.listeners.clear();
  }

  private onMessage(msg: ServerMsg): void {
    switch (msg.t) {
      case 'welcome':
        // welcome{ok:false} is always terminal (ROOM_NOT_FOUND/BAD_SEAT_TICKET/PROTOCOL_MISMATCH …).
        if (!msg.ok) this.markFatal(msg.error ?? 'incompatible');
        break;
      case 'seatAssigned':
        break;
      case 'snapshot':
        this.phaseN = 'playing'; // the match has started — leave the waiting room for the live board.
        this.finalScoresN = null; // a fresh match (incl. rematch) supersedes the last standings.
        this.snap = msg.state;
        this.drawPeekTile = msg.drawPeek ?? null;
        this.waitKind = msg.wait;
        this.legal = msg.legalCalls ?? [];
        this.ws.noteAck(msg.seq);
        break;
      case 'handStart':
        this.kyokuN = msg.kyoku;
        this.resultDetail = null;
        this.res = null;
        this.journal.append(msg.kyoku, {
          kind: 'handStart',
          kyoku: msg.kyoku,
          dealer: msg.dealer,
          honba: msg.honba,
          roundWind: msg.roundWind,
          scores: [...msg.scores],
          doraIndicators: [...msg.doraIndicators],
          myHand: [...(this.snap?.players[this.humanSeat]?.hand ?? [])],
        });
        this.ws.noteAck(msg.seq);
        break;
      case 'event':
        this.journal.appendEngine(this.kyokuN, [msg.ev as unknown as GameEvent]);
        this.ws.noteAck(msg.seq);
        break;
      case 'eventBatch':
        this.journal.appendEngine(this.kyokuN, msg.evs as unknown as GameEvent[]);
        for (const ev of msg.evs) {
          if (ev.event === 'win' || ev.event === 'ryuukyoku') this.resultDetail = ev;
        }
        this.ws.noteAck(msg.seq);
        break;
      case 'handEnd':
        this.res = {
          kind: msg.result,
          payments: msg.deltas,
          winners: msg.winners,
          detail: this.resultDetail as unknown as GameEvent | null,
        };
        this.waitKind = 'handOver';
        this.journal.append(this.kyokuN, {
          kind: 'handEnd',
          result: msg.result,
          winners: [...msg.winners],
          deltas: [...msg.deltas],
          scores: [...msg.scores],
        });
        this.ws.noteAck(msg.seq);
        break;
      case 'gameOver':
        this.waitKind = 'gameOver';
        this.finalScoresN = [...msg.scores]; // retained for the standings table through the rematch reset.
        // the journal's gameOver marker (shared with local play / fx) only models 'tobi'; the server's
        // richer reason (endOfGame/abort) is reflected in the standings UI, not this fx marker.
        this.journal.append(this.kyokuN, { kind: 'gameOver', reason: 'tobi', scores: [...msg.scores] });
        this.ws.noteAck(msg.seq);
        break;
      case 'choices':
        break;
      case 'roomState':
        // live waiting-lobby occupancy + the room code. If the server says the match is already started
        // (reconnect into a running game), reflect that immediately; the snapshot makes it authoritative.
        // After gameOver the server resets started→false → drop back to the waiting (rematch) lobby.
        this.roomState = msg.room;
        if (msg.room.started) this.phaseN = 'playing';
        else if (this.phaseN === 'playing') this.phaseN = 'waiting';
        break;
      case 'error':
        // fatal errors stop reconnect + show a terminal screen; non-fatal ones surface transiently.
        if (msg.fatal) this.markFatal(msg.detail ?? msg.code);
        else this.lastError = msg.detail ?? msg.code;
        break;
      case 'pong':
        break;
      case 'stamp':
        // volatile display-only: append (capped), do NOT noteAck (not in the ack/replay timeline).
        this.recentStamps = [...this.recentStamps.slice(-15), { seat: msg.seat, stamp: msg.stamp, at: Date.now() }];
        break;
    }
    this.notify();
  }

  private canUseDecisionHelpers(): boolean {
    return this.waitKind === 'turn' && this.snap !== null && this.snap.turn === this.humanSeat;
  }

  private decisionHand(): TileId[] {
    return vh.decisionHand(this.myHand(), this.humanDrawn());
  }

  private myHand(): TileId[] {
    return this.snap?.players[this.humanSeat]?.hand ?? [];
  }

  private myMelds(): GameStateView['players'][number]['melds'] {
    return this.snap?.players[this.humanSeat]?.melds ?? [];
  }

  /** Enter the terminal state: record the code/detail and STOP reconnecting (close the socket). */
  private markFatal(reason: string): void {
    this.fatal = reason;
    this.ws.close();
  }

  private notify(): void {
    for (const l of this.listeners) l();
  }
}
